import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AppDialogProvider } from './contexts/AppDialogContext';
import SentryUserSync from './components/SentryUserSync';
import AppToaster from './components/AppToaster';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import { csvHotelReportsBase, pmsHotelReportsBase } from './utils/propertyReportsPaths';

/** Bookmark compatibility: /pms-suppliers/:sid/hotels/:code/* → /properties/pms/... */
function LegacyPmsHotelReportsRedirect() {
  const { supplierId, hotelCode, '*': rest } = useParams();
  const base = pmsHotelReportsBase(supplierId, hotelCode);
  if (!base) return <Navigate to="/properties" replace />;
  const suffix = rest && String(rest).length ? String(rest) : 'reports';
  return <Navigate to={`${base}/${suffix}`} replace />;
}

/** Bookmark compatibility: /pms-csv-hotels/:id/* → /properties/csv/... */
function LegacyCsvHotelReportsRedirect() {
  const { csvHotelId, '*': rest } = useParams();
  const base = csvHotelReportsBase(csvHotelId);
  if (!base) return <Navigate to="/properties" replace />;
  const suffix = rest && String(rest).length ? String(rest) : 'reports';
  return <Navigate to={`${base}/${suffix}`} replace />;
}

/** Preserve query string when redirecting legacy /compliance paths. */
function LegacyCompliancePathRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search || ''}`} replace />;
}

/** Bookmark compatibility: /compliance/tasks/:id → /tasks/:id */
function LegacyComplianceTaskRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/tasks/${id}${location.search || ''}`} replace />;
}

/** Bookmark compatibility: /compliance/tasks/:id/edit → /tasks/:id/edit */
function LegacyComplianceTaskEditRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/tasks/${id}/edit${location.search || ''}`} replace />;
}

/** Bookmark compatibility: /compliance/entities/... → /tasks/entities/... */
function LegacyComplianceEntityRedirect() {
  const { entityType, entityKey } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/tasks/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityKey)}${location.search || ''}`}
      replace
    />
  );
}

// Lazy load all page components for code-splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InfrastructureTax = lazy(() => import('./pages/InfrastructureTax'));
const InfrastructureTaxCompliance = lazy(() => import('./pages/InfrastructureTaxCompliance'));
const InfrastructureTaxProperties = lazy(() => import('./pages/InfrastructureTaxProperties'));
const Properties = lazy(() => import('./pages/Properties'));
const PropertiesCompare = lazy(() => import('./pages/PropertiesCompare'));
const AllHotelsMonthlyTaxSummary = lazy(() => import('./pages/AllHotelsMonthlyTaxSummary'));
const PropertiesDetail = lazy(() => import('./pages/PropertiesDetail'));
const PropertyVatReport = lazy(() => import('./pages/PropertyVatReport'));
const PropertyReports = lazy(() => import('./pages/PropertyReports'));
const PmsHotelYearlyReport = lazy(() => import('./pages/PmsHotelYearlyReport'));
const PmsHotelReportsOverview = lazy(() => import('./pages/PmsHotelReportsOverview'));
const PropertyRevenueReport = lazy(() => import('./pages/PropertyRevenueReport'));
const PropertyBookingsReport = lazy(() => import('./pages/PropertyBookingsReport'));
const PropertyComplianceReport = lazy(() => import('./pages/PropertyComplianceReport'));
const PropertyAnalyticsReport = lazy(() => import('./pages/PropertyAnalyticsReport'));
const CsvHotelRevenueReport = lazy(() => import('./pages/CsvHotelRevenueReport'));
const ComplianceEntityDetail = lazy(() => import('./pages/ComplianceEntityDetail'));
const ComplianceTasksList = lazy(() => import('./pages/ComplianceTasksList'));
const ComplianceTaskView = lazy(() => import('./pages/ComplianceTaskView'));
const ComplianceTaskEdit = lazy(() => import('./pages/ComplianceTaskEdit'));
const ComplianceTemplates = lazy(() => import('./pages/ComplianceTemplates'));
const RegionalVat = lazy(() => import('./pages/RegionalVat'));
const Channels = lazy(() => import('./pages/Channels'));
const ChannelsDetail = lazy(() => import('./pages/ChannelsDetail'));
const AdminPmsSupplierManagement = lazy(() => import('./pages/admin/AdminPmsSupplierManagement'));
const PmsSuppliersDirectory = lazy(() => import('./pages/PmsSuppliersDirectory'));
const PmsSuppliersDetail = lazy(() => import('./pages/PmsSuppliersDetail'));
const PmsSupplierHotels = lazy(() => import('./pages/PmsSupplierHotels'));
const Bookings = lazy(() => import('./pages/Bookings'));
const BookingsDetail = lazy(() => import('./pages/BookingsDetail'));
const Visitors = lazy(() => import('./pages/Visitors'));
const VisitorsDetail = lazy(() => import('./pages/VisitorsDetail'));
const Search = lazy(() => import('./pages/Search'));
const AdminFtoComplianceReport = lazy(() => import('./pages/admin/AdminFtoComplianceReport'));
const RolePermissionsGuide = lazy(() => import('./pages/admin/RolePermissionsGuide'));
const FtoEntities = lazy(() => import('./pages/FtoEntities'));
const TaxpayersList = lazy(() => import('./pages/TaxpayersList'));
const TaxpayerProfile = lazy(() => import('./pages/TaxpayerProfile'));
const TaxpayerEdit = lazy(() => import('./pages/TaxpayerEdit'));
const PropertyProfile = lazy(() => import('./pages/PropertyProfile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const HelpHub = lazy(() => import('./pages/help/HelpHub'));
const HelpArticle = lazy(() => import('./pages/help/HelpArticle'));
const HelpManage = lazy(() => import('./pages/help/HelpManage'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <AppDialogProvider>
        <SentryUserSync />
        <AppToaster />

        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="vat" element={
                <ProtectedRoute sidebarMenuKey="vat" permission="dashboard.view_tax_totals">
                  <RegionalVat />
                </ProtectedRoute>
              } />
              <Route path="infrastructure-tax" element={
                <ProtectedRoute
                  sidebarMenuKey="infrastructure-tax"
                  permission="dashboard.view_tax_totals"
                >
                  <InfrastructureTax />
                </ProtectedRoute>
              } />
              <Route path="infrastructure-tax/compliance" element={
                <ProtectedRoute
                  sidebarMenuKey="infrastructure-tax"
                  permission="dashboard.view_tax_totals"
                >
                  <InfrastructureTaxCompliance />
                </ProtectedRoute>
              } />
              <Route path="infrastructure-tax/properties" element={
                <ProtectedRoute
                  sidebarMenuKey="infrastructure-tax"
                  permission="dashboard.view_tax_totals"
                >
                  <InfrastructureTaxProperties />
                </ProtectedRoute>
              } />
              <Route path="tasks" element={<ComplianceTasksList />} />
              <Route path="tasks/new" element={
                <ProtectedRoute permission="tasks.create">
                  <ComplianceTaskEdit isCreate />
                </ProtectedRoute>
              } />
              <Route path="tasks/templates" element={
                <ProtectedRoute permission="tasks.manage_templates">
                  <ComplianceTemplates />
                </ProtectedRoute>
              } />
              <Route path="tasks/entities/:entityType/:entityKey" element={<ComplianceEntityDetail />} />
              <Route path="tasks/:id/edit" element={
                <ProtectedRoute permissions={['tasks.edit', 'tasks.create']}>
                  <ComplianceTaskEdit />
                </ProtectedRoute>
              } />
              <Route path="tasks/:id" element={<ComplianceTaskView />} />
              {/* Legacy /compliance/* bookmarks → /tasks/* */}
              <Route path="compliance" element={<Navigate to="/tasks" replace />} />
              <Route path="compliance/properties" element={<Navigate to="/properties" replace />} />
              <Route path="compliance/templates" element={<Navigate to="/tasks/templates" replace />} />
              <Route path="compliance/tasks" element={<LegacyCompliancePathRedirect to="/tasks" />} />
              <Route path="compliance/tasks/new" element={<LegacyCompliancePathRedirect to="/tasks/new" />} />
              <Route path="compliance/tasks/:id/edit" element={<LegacyComplianceTaskEditRedirect />} />
              <Route path="compliance/tasks/:id" element={<LegacyComplianceTaskRedirect />} />
              <Route path="compliance/entities/:entityType/:entityKey" element={<LegacyComplianceEntityRedirect />} />
              <Route path="compliance/*" element={<Navigate to="/tasks" replace />} />
              <Route path="properties" element={
                <ProtectedRoute permission="properties.view">
                  <Properties />
                </ProtectedRoute>
              } />
              <Route path="properties/compare" element={
                <ProtectedRoute permission="properties.view">
                  <PropertiesCompare />
                </ProtectedRoute>
              } />
              <Route path="properties/reports/all-hotels-monthly" element={
                <ProtectedRoute permissions={['reports.view_portfolio', 'reports.view_tax']}>
                  <AllHotelsMonthlyTaxSummary />
                </ProtectedRoute>
              } />
              <Route path="properties/crm/:registry/:id" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyProfile />
                </ProtectedRoute>
              } />
              {/* Hotel report hubs under /properties (before :id so "pms"/"csv" are not treated as ids) */}
              <Route path="properties/pms/:supplierId/:hotelCode/reports/overview" element={
                <ProtectedRoute permission="properties.view">
                  <PmsHotelReportsOverview />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/reports/yearly" element={
                <ProtectedRoute permission="properties.view">
                  <PmsHotelYearlyReport />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/reports" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyReports />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/vat-report" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyVatReport />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/reports/revenue" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyRevenueReport />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/reports/bookings" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyBookingsReport />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/reports/compliance" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyComplianceReport />
                </ProtectedRoute>
              } />
              <Route path="properties/pms/:supplierId/:hotelCode/reports/analytics" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyAnalyticsReport />
                </ProtectedRoute>
              } />
              <Route path="properties/csv/:csvHotelId/reports" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyReports />
                </ProtectedRoute>
              } />
              <Route path="properties/csv/:csvHotelId/reports/revenue" element={
                <ProtectedRoute permission="properties.view">
                  <CsvHotelRevenueReport />
                </ProtectedRoute>
              } />
              <Route path="properties/:id" element={
                <ProtectedRoute permission="properties.view">
                  <PropertiesDetail />
                </ProtectedRoute>
              } />
              <Route path="properties/:id/reports" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyReports />
                </ProtectedRoute>
              } />
              <Route path="properties/:id/vat-report" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyVatReport />
                </ProtectedRoute>
              } />
              <Route path="properties/:id/reports/revenue" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyRevenueReport />
                </ProtectedRoute>
              } />
              <Route path="properties/:id/reports/bookings" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyBookingsReport />
                </ProtectedRoute>
              } />
              <Route path="properties/:id/reports/compliance" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyComplianceReport />
                </ProtectedRoute>
              } />
              <Route path="properties/:id/reports/analytics" element={
                <ProtectedRoute permission="properties.view">
                  <PropertyAnalyticsReport />
                </ProtectedRoute>
              } />
              <Route path="channels" element={<Channels />} />
              <Route path="channels/:id" element={<ChannelsDetail />} />
              <Route
                path="admin/pms-suppliers/:id/hotels"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <PmsSupplierHotels />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pms-suppliers/:id"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <PmsSuppliersDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pms-suppliers"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <AdminPmsSupplierManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pms-suppliers/:id/hotels"
                element={
                  <ProtectedRoute requirePmsSuppliersAccess>
                    <PmsSupplierHotels />
                  </ProtectedRoute>
                }
              />
              <Route
                path="taxpayers"
                element={
                  <ProtectedRoute permission="taxpayers.view">
                    <TaxpayersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="taxpayers/new"
                element={
                  <ProtectedRoute permission="taxpayers.create">
                    <TaxpayerEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="taxpayers/:id/edit"
                element={
                  <ProtectedRoute permission="taxpayers.edit">
                    <TaxpayerEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="taxpayers/:id"
                element={
                  <ProtectedRoute permission="taxpayers.view">
                    <TaxpayerProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="fto-entities"
                element={
                  <ProtectedRoute requireFtoEntitiesAccess>
                    <FtoEntities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pms-suppliers/:id"
                element={
                  <ProtectedRoute requirePmsSuppliersAccess>
                    <PmsSuppliersDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pms-suppliers"
                element={
                  <ProtectedRoute requirePmsSuppliersAccess>
                    <PmsSuppliersDirectory />
                  </ProtectedRoute>
                }
              />
              {/* Legacy hotel report URLs → /properties/pms|csv/... */}
              <Route path="pms-suppliers/:supplierId/hotels/:hotelCode/*" element={<LegacyPmsHotelReportsRedirect />} />
              <Route path="pms-csv-hotels/:csvHotelId/*" element={<LegacyCsvHotelReportsRedirect />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="bookings/:id" element={<BookingsDetail />} />
              <Route
                path="visitors"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <Visitors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="visitors/:id"
                element={
                  <ProtectedRoute requireSuperAdmin>
                    <VisitorsDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="search" element={<Search />} />
              <Route
                path="help/manage/:articleId"
                element={
                  <ProtectedRoute permission="help.manage">
                    <HelpManage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="help/manage"
                element={
                  <ProtectedRoute permission="help.manage">
                    <HelpManage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="help/:slug"
                element={
                  <ProtectedRoute permission="help.view">
                    <HelpArticle />
                  </ProtectedRoute>
                }
              />
              <Route
                path="help"
                element={
                  <ProtectedRoute permission="help.view">
                    <HelpHub />
                  </ProtectedRoute>
                }
              />
              <Route path="admin" element={<AdminDashboard />} />
              <Route
                path="admin/role-guide"
                element={
                  <ProtectedRoute permission="users.view">
                    <RolePermissionsGuide />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/fto-compliance-report"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminFtoComplianceReport />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </Suspense>
        </Router>
      </AppDialogProvider>
    </AuthProvider>
  );
}

export default App;

